import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Textarea } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { can, useMe } from '../lib/session';
import type {
  OssThreshold,
  PaymentMethodConfig,
  ShippingMethodRow,
  StoreSettings,
} from '../lib/types';
import { formatMoney, parseMoney } from '../lib/utils';

/** Настройки магазина: реквизиты, адрес возвратов, оплата переводом, доставка, способы оплаты. */
export function SettingsPage() {
  const { t } = useTranslation();
  const me = useMe();
  return (
    <>
      <PageHeader title={t('set.title')} description={t('set.hint')} />
      <div className="grid gap-6">
        {can(me, 'settings.manage') ? <StoreDetails /> : null}
        {can(me, 'settings.manage') ? <VatOss /> : null}
        {can(me, 'settings.manage') ? <ShippingPricing /> : null}
        {can(me, 'markets.manage') ? <ShippingMethods /> : null}
        {can(me, 'markets.manage') ? <PaymentMethods /> : null}
      </div>
    </>
  );
}

function StoreDetails() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<StoreSettings>('/settings'),
  });
  const s = settings.data;
  const [form, setForm] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const value = (key: string, fallback: string | undefined) => form[key] ?? fallback ?? '';
  const set = (key: string) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const save = async (
    key: 'seller' | 'returns' | 'bank_transfer' | 'fraud' | 'catalog',
    body: unknown,
  ) => {
    setError(undefined);
    setMessage(undefined);
    try {
      await api(`/settings/${key}`, { method: 'PUT', body });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setMessage(t('common.saved'));
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  if (!s) return null;
  const optional = (v: string) => (v.trim() ? v.trim() : undefined);

  return (
    <Card>
      <CardHeader title={t('set.store')} />
      <CardContent className="grid gap-4 text-sm">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t('set.sellerName')}>
            {(id) => <Input id={id} value={value('name', s.seller?.name)} onChange={set('name')} />}
          </Field>
          <Field label={t('set.regNo')}>
            {(id) => (
              <Input id={id} value={value('regNo', s.seller?.regNo)} onChange={set('regNo')} />
            )}
          </Field>
          <Field label={t('set.vatId')}>
            {(id) => (
              <Input id={id} value={value('vatId', s.seller?.vatId)} onChange={set('vatId')} />
            )}
          </Field>
          <Field label={t('set.bank')}>
            {(id) => <Input id={id} value={value('bank', s.seller?.bank)} onChange={set('bank')} />}
          </Field>
          <Field label={t('set.sellerPhone')} hint={t('set.sellerPhoneHint')}>
            {(id) => (
              <Input
                id={id}
                type="tel"
                value={value('phone', s.seller?.phone)}
                onChange={set('phone')}
              />
            )}
          </Field>
          <Field label={t('set.sellerEmail')}>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={value('email', s.seller?.email)}
                onChange={set('email')}
              />
            )}
          </Field>
          <Field label={t('set.address')} hint={t('set.addressHint')} className="md:col-span-2">
            {(id) => (
              <Textarea
                id={id}
                rows={3}
                value={value('address', s.seller?.address.join('\n'))}
                onChange={set('address')}
              />
            )}
          </Field>
        </div>
        <Button
          className="justify-self-start"
          onClick={() =>
            void save('seller', {
              // Структурный адрес для e-invoice в форме не редактируется — сохраняем как был.
              street: s.seller?.street,
              city: s.seller?.city,
              postalCode: s.seller?.postalCode,
              name: value('name', s.seller?.name),
              address: value('address', s.seller?.address.join('\n'))
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean),
              regNo: optional(value('regNo', s.seller?.regNo)),
              vatId: optional(value('vatId', s.seller?.vatId)),
              bank: optional(value('bank', s.seller?.bank)),
              email: optional(value('email', s.seller?.email)),
              phone: optional(value('phone', s.seller?.phone)),
              countryCode: 'LV',
            })
          }
        >
          {t('set.saveSeller')}
        </Button>
        <div className="flex flex-wrap items-end gap-2 border-t pt-4">
          <Field label={t('set.returnsAddress')} className="min-w-80 flex-1">
            {(id) => (
              <Input
                id={id}
                value={value('returns', s.returns?.address)}
                onChange={set('returns')}
              />
            )}
          </Field>
          <Button
            variant="outline"
            onClick={() => void save('returns', { address: value('returns', s.returns?.address) })}
          >
            {t('common.save')}
          </Button>
          <Field label={t('set.holdDays')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={1}
                max={30}
                value={value('holdDays', s.bank_transfer?.holdDays.toString() ?? '5')}
                onChange={set('holdDays')}
                className="w-24"
              />
            )}
          </Field>
          <Button
            variant="outline"
            onClick={() =>
              void save('bank_transfer', {
                holdDays: Number(value('holdDays', s.bank_transfer?.holdDays.toString() ?? '5')),
              })
            }
          >
            {t('common.save')}
          </Button>
        </div>
        <div className="grid gap-2 border-t pt-4">
          <Field label={t('set.blocked')} hint={t('set.blockedHint')}>
            {(id) => (
              <Textarea
                id={id}
                rows={4}
                value={value('blocked', s.fraud?.blocked.join('\n'))}
                onChange={set('blocked')}
              />
            )}
          </Field>
          <Button
            variant="outline"
            className="justify-self-start"
            onClick={() =>
              void save('fraud', {
                blocked: value('blocked', s.fraud?.blocked.join('\n'))
                  .split('\n')
                  .map((l) => l.trim())
                  .filter(Boolean),
              })
            }
          >
            {t('common.save')}
          </Button>
        </div>
        <label className="flex items-start gap-2 border-t pt-4 text-sm">
          <Checkbox
            checked={s.catalog?.requireManufacturer ?? true}
            onChange={(e) => void save('catalog', { requireManufacturer: e.target.checked })}
          />
          <span>
            {t('set.requireManufacturer')}
            <span className="block text-muted-foreground">{t('set.requireManufacturerHint')}</span>
          </span>
        </label>
        {message ? <Alert variant="success">{message}</Alert> : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </CardContent>
    </Card>
  );
}

/**
 * OSS: без регистрации и ниже порога 10 000 € в год продажи частным лицам в другие страны ЕС
 * идут с НДС Латвии; после порога сайт сам берёт НДС страны покупателя.
 */
function VatOss() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<StoreSettings>('/settings'),
  });
  const status = useQuery({
    queryKey: ['settings', 'oss-threshold'],
    queryFn: () => api<OssThreshold>('/accounting/oss-threshold'),
  });
  const [form, setForm] = useState<{ registered?: boolean; other?: string; previous?: string }>({});
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const vat = settings.data?.vat;
  const registered = form.registered ?? vat?.ossRegistered ?? true;
  const other = form.other ?? String(vat?.otherChannelsEur ?? 0);
  const previous = form.previous ?? String(vat?.previousYearEur ?? 0);
  const save = async () => {
    setError(undefined);
    setMessage(undefined);
    try {
      await api('/settings/vat', {
        method: 'PUT',
        body: {
          ossRegistered: registered,
          otherChannelsEur: Number(other) || 0,
          previousYearEur: Number(previous) || 0,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setMessage(t('common.saved'));
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const s = status.data;
  const eur = (v: number) => formatMoney(Math.round(v * 100), 'EUR');
  return (
    <Card>
      <CardHeader title={t('set.oss')} description={t('set.ossHint')} />
      <CardContent className="grid max-w-xl gap-3">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={registered}
            onChange={(e) => {
              setForm((f) => ({ ...f, registered: e.target.checked }));
            }}
          />
          {t('set.ossRegistered')}
        </label>
        <Field label={t('set.ossOther')} hint={t('set.ossOtherHint')}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              value={other}
              onChange={(e) => {
                setForm((f) => ({ ...f, other: e.target.value }));
              }}
              className="w-40"
            />
          )}
        </Field>
        <Field label={t('set.ossPrevious')}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              value={previous}
              onChange={(e) => {
                setForm((f) => ({ ...f, previous: e.target.value }));
              }}
              className="w-40"
            />
          )}
        </Field>
        <Button variant="outline" className="justify-self-start" onClick={() => void save()}>
          {t('common.save')}
        </Button>
        {s ? (
          <p className="text-sm" data-testid="oss-threshold">
            {t('set.ossStatus', {
              year: s.year,
              shop: eur(s.shopEur),
              total: eur(s.totalEur),
              threshold: eur(s.thresholdEur),
            })}{' '}
            <Badge>{s.destinationVat ? t('set.ossDestination') : t('set.ossOrigin')}</Badge>
          </p>
        ) : null}
        {s && !s.ossRegistered && s.destinationVat ? (
          <Alert variant="destructive">{t('set.ossOver')}</Alert>
        ) : null}
        {message ? <Alert variant="success">{message}</Alert> : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </CardContent>
    </Card>
  );
}

const CARRIERS = ['venipak', 'omniva', 'dpd', 'latvijas_pasts'] as const;
const SURCHARGES = ['fuelSurchargePct', 'labourSurchargePct', 'otherSurchargePct'] as const;
type SurchargeKind = (typeof SURCHARGES)[number];

/** Наценка магазина и топливные надбавки перевозчиков — к тарифам без НДС. */
function ShippingPricing() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<StoreSettings>('/settings'),
  });
  const [form, setForm] = useState<{
    markup?: string;
    courierMin?: string;
    pct?: Record<string, string>;
  }>({});
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const current = settings.data?.shipping_pricing;
  const markup = form.markup ?? ((current?.markupMinor ?? 0) / 100).toFixed(2);
  const courierMin = form.courierMin ?? ((current?.courierMinMinor ?? 600) / 100).toFixed(2);
  const pct = (kind: SurchargeKind, carrier: string) =>
    form.pct?.[`${kind}.${carrier}`] ?? String(current?.[kind]?.[carrier] ?? 0);
  const record = (kind: SurchargeKind) =>
    Object.fromEntries(CARRIERS.map((c) => [c, Number(pct(kind, c).replace(',', '.')) || 0]));
  const save = async () => {
    setError(undefined);
    setMessage(undefined);
    try {
      await api('/settings/shipping_pricing', {
        method: 'PUT',
        body: {
          markupMinor: parseMoney(markup) ?? 0,
          courierMinMinor: parseMoney(courierMin) ?? 0,
          fuelSurchargePct: record('fuelSurchargePct'),
          labourSurchargePct: record('labourSurchargePct'),
          otherSurchargePct: record('otherSurchargePct'),
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setMessage(t('common.saved'));
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <Card>
      <CardHeader title={t('set.shippingPricing')} description={t('set.shippingPricingHint')} />
      <CardContent className="grid max-w-2xl gap-3">
        <Field label={t('set.markup')}>
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              value={markup}
              onChange={(e) => {
                setForm((f) => ({ ...f, markup: e.target.value }));
              }}
              className="w-40"
            />
          )}
        </Field>
        <Field label={t('set.courierMin')} hint={t('set.courierMinHint')}>
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              value={courierMin}
              onChange={(e) => {
                setForm((f) => ({ ...f, courierMin: e.target.value }));
              }}
              className="w-40"
            />
          )}
        </Field>
        <table className="text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 font-normal">{t('set.carrierCol')}</th>
              {SURCHARGES.map((k) => (
                <th key={k} className="py-1 font-normal">
                  {t(`set.surcharge.${k}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CARRIERS.map((c) => (
              <tr key={c}>
                <td className="py-1 pr-2">{t(`set.carrier.${c}`)}</td>
                {SURCHARGES.map((k) => (
                  <td key={k} className="py-1 pr-2">
                    <Input
                      aria-label={`${t(`set.carrier.${c}`)}: ${t(`set.surcharge.${k}`)}`}
                      inputMode="decimal"
                      value={pct(k, c)}
                      onChange={(e) => {
                        setForm((f) => ({
                          ...f,
                          pct: { ...f.pct, [`${k}.${c}`]: e.target.value },
                        }));
                      }}
                      className="w-24"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <Button variant="outline" className="justify-self-start" onClick={() => void save()}>
          {t('common.save')}
        </Button>
        {message ? <Alert variant="success">{message}</Alert> : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </CardContent>
    </Card>
  );
}

/** Тарифы: «flat» — одна цена; «weight» — строки «до граммов: цена». */
function rulesToText(rules: ShippingMethodRow['priceRules']): string {
  if (rules.type === 'flat') return (rules.amountMinor / 100).toFixed(2);
  return rules.brackets
    .map((b) => `${String(b.upToG)}: ${(b.amountMinor / 100).toFixed(2)}`)
    .join('\n');
}

function textToRules(text: string): ShippingMethodRow['priceRules'] | undefined {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 1 && !lines[0]?.includes(':')) {
    const amount = parseMoney(lines[0] ?? '');
    return amount === undefined ? undefined : { type: 'flat', amountMinor: amount };
  }
  const brackets = [];
  for (const line of lines) {
    const [g, price] = line.split(':');
    const amount = parseMoney(price?.trim() ?? '');
    const upToG = Number(g?.trim());
    if (amount === undefined || !Number.isInteger(upToG) || upToG <= 0) return undefined;
    brackets.push({ upToG, amountMinor: amount });
  }
  return brackets.length ? { type: 'weight', brackets } : undefined;
}

function ShippingMethods() {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState<ShippingMethodRow>();
  const methods = useQuery({
    queryKey: ['shipping-methods'],
    queryFn: () => api<{ items: ShippingMethodRow[] }>('/shipping-methods'),
  });
  return (
    <Card>
      <CardHeader title={t('set.shipping')} description={t('set.shippingHint')} />
      <CardContent>
        <table className="w-full text-sm">
          <tbody>
            {(methods.data?.items ?? []).map((m) => (
              <tr key={m.id} className="border-b last:border-0">
                <td className="py-1.5">
                  <Badge variant="outline">{m.marketCode}</Badge>
                </td>
                <td>{m.names[i18n.language] ?? m.names.en ?? m.code}</td>
                <td className="text-muted-foreground">{m.carrier}</td>
                <td>
                  {m.priceRules.type === 'flat'
                    ? formatMoney(m.priceRules.amountMinor, m.currency, i18n.language)
                    : t('set.byWeight', { count: m.priceRules.brackets.length })}
                </td>
                <td>
                  {m.freeFromMinor !== null
                    ? t('set.freeFrom', {
                        amount: formatMoney(m.freeFromMinor, m.currency, i18n.language),
                      })
                    : ''}
                </td>
                <td>
                  {m.deliveryDaysMin}–{m.deliveryDaysMax} {t('set.days')}
                </td>
                <td>
                  {m.isActive ? null : <Badge variant="secondary">{t('common.inactive')}</Badge>}
                </td>
                <td className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditing(m);
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      {editing ? (
        <ShippingDialog
          method={editing}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
    </Card>
  );
}

function ShippingDialog({ method, onClose }: { method: ShippingMethodRow; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [rules, setRules] = useState(rulesToText(method.priceRules));
  const [free, setFree] = useState(
    method.freeFromMinor === null ? '' : (method.freeFromMinor / 100).toFixed(2),
  );
  const [min, setMin] = useState(String(method.deliveryDaysMin));
  const [max, setMax] = useState(String(method.deliveryDaysMax));
  const [active, setActive] = useState(method.isActive);
  const [error, setError] = useState<string>();
  const parsed = textToRules(rules);

  const save = async () => {
    setError(undefined);
    try {
      await api(`/shipping-methods/${method.id}`, {
        method: 'PATCH',
        body: {
          // Тариф из текста; цены без НДС, ограничения (ячейка пакомата) и зоны — как были.
          priceRules: parsed && {
            ...parsed,
            ...(method.priceRules.basis ? { basis: method.priceRules.basis } : {}),
            ...(method.priceRules.limits ? { limits: method.priceRules.limits } : {}),
            ...(parsed.type === 'weight' &&
            method.priceRules.type === 'weight' &&
            method.priceRules.zones
              ? { zones: method.priceRules.zones }
              : {}),
          },
          freeFromMinor: free.trim() ? parseMoney(free) : null,
          deliveryDaysMin: Number(min),
          deliveryDaysMax: Number(max),
          isActive: active,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['shipping-methods'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${method.marketCode} · ${method.code}`}
      footer={
        <Button disabled={!parsed} onClick={() => void save()}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="grid gap-3 text-sm">
        <Field
          label={t('set.tariff')}
          hint={`${t('set.tariffHint')}${method.priceRules.basis === 'net' ? ` ${t('set.tariffNet')}` : ''}`}
        >
          {(id) => (
            <Textarea
              id={id}
              rows={4}
              value={rules}
              onChange={(e) => {
                setRules(e.target.value);
              }}
              className="font-mono"
            />
          )}
        </Field>
        <Field label={t('set.freeFromLabel')}>
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              value={free}
              onChange={(e) => {
                setFree(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex gap-2">
          <Field label={t('set.daysMin')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                value={min}
                onChange={(e) => {
                  setMin(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('set.daysMax')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                value={max}
                onChange={(e) => {
                  setMax(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={active}
            onChange={(e) => {
              setActive(e.target.checked);
            }}
          />
          {t('common.active')}
        </label>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}

function PaymentMethods() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>();
  const methods = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => api<{ items: PaymentMethodConfig[] }>('/payment-methods'),
  });
  const toggle = async (id: string, isActive: boolean) => {
    setError(undefined);
    try {
      await api(`/payment-methods/${id}`, { method: 'PATCH', body: { isActive } });
      await queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <Card>
      <CardHeader title={t('set.payments')} description={t('set.paymentsHint')} />
      <CardContent className="grid gap-1 text-sm">
        {(methods.data?.items ?? []).map(({ config, marketCode }) => (
          <label key={config.id} className="flex items-center gap-2">
            <Checkbox
              checked={config.isActive}
              onChange={(e) => void toggle(config.id, e.target.checked)}
            />
            <Badge variant="outline">{marketCode}</Badge>
            {config.provider}:{config.method}
            {config.b2bOnly ? <Badge variant="secondary">B2B</Badge> : null}
          </label>
        ))}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </CardContent>
    </Card>
  );
}
